import { Box, TextField, Typography, Autocomplete, CircularProgress, Button } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import React, { useCallback, useEffect, useState } from 'react';
import axiosService from 'utils/axios';
import { Company } from 'types/entities/types';
import { Destination } from '../../../types/entities/destination_type';
import { Notify } from '../../../types/entities/notify_type';
import ModalPopup from '../../../components/Modal/ModalPopup';
import AddOrEditCompany from '../companies/AddOrEditCompany';
import AddOrEditDestination from '../destinations/AddOrEditDestination';

interface Props {
  notify?: Notify;
  onSave: (notify: Notify) => void;
  modalTitle: string;
  isModalOpen: boolean;
  setModalOpen: (open: boolean) => void;
}

interface NotifyEntity {
  id: string;
  name: string;
}

interface NotifyCreateUpdateError {
  error: string;
}

export default function AddOrEditNotify(props: Props) {
  const { notify, onSave, modalTitle, isModalOpen, setModalOpen } = props;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<NotifyCreateUpdateError | null>();

  const [companyOptions, setCompanyOptions] = useState<NotifyEntity[]>([]);
  const [isCompanyOptionsLoading, setIsCompanyOptionsLoading] = useState(false);

  const [destinationOptions, setDestinationOptions] = useState<NotifyEntity[]>([]);
  const [isDestinationOptionsLoading, setIsDestinationOptionsLoading] = useState(false);

  const [companyTextInput, setCompanyTextInput] = useState<string>();
  const [addNewCompanyModalOpen, setAddNewCompanyModalOpen] = useState(false);
  const [companyChoice, setCompanyChoice] = useState<NotifyEntity | null>(
    notify?.company
      ? {
          id: notify.company.id,
          name: notify.company.name,
        }
      : null
  );

  const [destinationTextInput, setDestinationTextInput] = useState<string>();
  const [addNewDestinationModalOpen, setAddNewDestinationModalOpen] = useState(false);
  const [destinationChoice, setDestinationChoice] = useState<NotifyEntity | null>(
    notify?.destination
      ? {
          id: notify.destination.id,
          name: notify.destination.name,
        }
      : null
  );

  const [notesInput, setNotesInput] = useState(notify?.notes || '');

  const getCompaniesList = useCallback((onSuccess?: () => void) => {
    setIsCompanyOptionsLoading(true);
    axiosService
      .get(`/entities/company/`)
      .then(res => {
        const names: NotifyEntity[] = [];
        res.data.forEach((company: Company) => names.push({ id: company.id, name: company.name }));
        setCompanyOptions(names);
        if (onSuccess) {
          onSuccess();
        }
      })
      .finally(() => setIsCompanyOptionsLoading(false));
  }, []);

  const getDestinationsList = useCallback((onSuccess?: () => void) => {
    setIsDestinationOptionsLoading(true);
    axiosService
      .get(`/entities/destination/`)
      .then(res => {
        const names: NotifyEntity[] = [];
        res.data.forEach((destination: Destination) => {
          names.push({ id: destination.id, name: destination.name });
        });
        setDestinationOptions(names);
        if (onSuccess) {
          onSuccess();
        }
      })
      .finally(() => setIsDestinationOptionsLoading(false));
  }, []);

  useEffect(() => {
    // Fetch companies list
    getCompaniesList();

    // Fetch destinations list
    getDestinationsList();
  }, [getCompaniesList, getDestinationsList]);

  const handleSubmit = () => {
    setLoading(true);
    const data = {
      // eslint-disable-next-line camelcase
      company_id: companyChoice?.id || '',
      // eslint-disable-next-line camelcase
      destination_id: destinationChoice?.id || '',
      notes: notesInput,
    };
    if (notify) {
      // Update existing notify
      axiosService
        .patch(`/entities/notify/${notify.id}/`, data)
        .then(notifyData => {
          setLoading(false);
          setError(null);
          onSave(notifyData.data);
        })
        .catch(err => {
          setLoading(false);
          setError(err.response.data || {});
        });
    } else {
      // Create new notify
      axiosService
        .post('/entities/notify/', data)
        .then(notifyData => {
          setLoading(false);
          setError(null);
          onSave(notifyData.data);
        })
        .catch(err => {
          setLoading(false);
          setError(err.response.data || {});
        });
    }
  };

  return (
    <ModalPopup
      title={modalTitle}
      open={isModalOpen}
      onClose={() => setModalOpen(false)}
      actionButtons={
        <LoadingButton
          loading={loading}
          onClick={handleSubmit}
          type="submit"
          fullWidth
          variant="contained"
          disabled={!companyChoice || !destinationChoice}
        >
          {notify ? 'Edit notify' : 'Create notify'}
        </LoadingButton>
      }
    >
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box sx={{ width: '100%', mt: 1 }}>
          {error ? <Typography>{error.error}</Typography> : null}
          <Autocomplete
            fullWidth
            sx={{ mt: 1, mb: 2 }}
            value={companyChoice}
            onChange={(event, value) => setCompanyChoice(value)}
            onInputChange={(event, value) => setCompanyTextInput(value)}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            getOptionLabel={option => option.name}
            options={companyOptions}
            loading={isCompanyOptionsLoading}
            renderInput={params => (
              <TextField
                {...params}
                required
                label="Company"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loading ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            noOptionsText={
              <Button fullWidth onClick={() => setAddNewCompanyModalOpen(true)}>
                Add a new Company
              </Button>
            }
          />
          {addNewCompanyModalOpen ? (
            <AddOrEditCompany
              companyName={companyTextInput}
              onSave={newCompany => {
                getCompaniesList(() => {
                  setCompanyChoice({
                    id: newCompany.id,
                    name: newCompany.name,
                  });
                  setAddNewCompanyModalOpen(false);
                });
              }}
              modalTitle="Add new company"
              isModalOpen={addNewCompanyModalOpen}
              setModalOpen={setAddNewCompanyModalOpen}
            />
          ) : null}
          <Autocomplete
            fullWidth
            sx={{ mb: 1 }}
            value={destinationChoice}
            onChange={(event, value) => setDestinationChoice(value)}
            onInputChange={(event, value) => setDestinationTextInput(value)}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            getOptionLabel={option => option.name}
            options={destinationOptions}
            loading={isDestinationOptionsLoading}
            renderInput={params => (
              <TextField
                {...params}
                required
                label="Destination"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loading ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            noOptionsText={
              <Button fullWidth onClick={() => setAddNewDestinationModalOpen(true)}>
                Add a new Destination
              </Button>
            }
          />
          {addNewDestinationModalOpen ? (
            <AddOrEditDestination
              destinationName={destinationTextInput}
              onSave={newDestination => {
                getDestinationsList(() => {
                  setDestinationChoice({
                    id: newDestination.id,
                    name: newDestination.name,
                  });
                  setAddNewDestinationModalOpen(false);
                });
              }}
              modalTitle="Add new destination"
              isModalOpen={addNewDestinationModalOpen}
              setModalOpen={setAddNewDestinationModalOpen}
            />
          ) : null}
          <TextField
            fullWidth
            minRows={5}
            multiline
            margin="normal"
            id="notes"
            label="Notes"
            value={notesInput}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setNotesInput(event.target.value);
            }}
          />
        </Box>
      </Box>
    </ModalPopup>
  );
}
